import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { Spinner } from './components/States';
import { ForgotPasswordPage, LoginPage, RegisterPage, ResetPasswordPage } from './pages/Auth';
import { OwnerLayout } from './pages/owner/OwnerLayout';
import { OwnerDashboardPage } from './pages/owner/Dashboard';
import { OwnerBusinessesPage } from './pages/owner/Businesses';
import { OwnerBranchesPage } from './pages/owner/Branches';
import { BranchCreatePage } from './pages/owner/BranchCreate';
import { BranchStaffPage } from './pages/owner/BranchStaff';
import { OwnerListingsPage } from './pages/owner/Listings';
import { ListingEditorPage } from './pages/owner/ListingEditor';
import { ListingManagePage } from './pages/owner/ListingManage';
import { OwnerEnquiriesPage } from './pages/owner/Enquiries';
import { OwnerBillingPage } from './pages/owner/Billing';
import { AccountPage } from './pages/owner/Account';

/** Where a customer account is sent, since this site has nothing for them. */
const CUSTOMER_SITE_URL = import.meta.env.VITE_CUSTOMER_WEB_URL || 'http://localhost:5173';

/**
 * Blocks a route until the session is known, then decides whether it qualifies.
 *
 * Admits everyone the API's "OwnerOnly" policy admits - an organisation owner and the
 * front-desk staff of one branch - because what each of them may see is decided per
 * request from their membership, not here. Checking for the Owner role alone turned a
 * branch login into a dead end on the very portal it was created for, and offered the
 * customer site as the only way out.
 *
 * A signed-in customer is shown an explanation rather than being redirected. Sending them
 * to "/" would bounce them straight back here through the root redirect, and they would
 * sit in a loop with nothing on screen telling them why.
 */
function RequirePartner({ children }: { children: JSX.Element }) {
  const { initialising, isAuthenticated, isPartner } = useAuth();
  const location = useLocation();

  if (initialising) {
    return (
      <div className="container py-5">
        <Spinner label="Checking your session" />
      </div>
    );
  }

  if (!isAuthenticated) {
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnTo=${returnTo}`} replace />;
  }

  if (!isPartner) {
    return <NotAPartnerAccount />;
  }

  return children;
}

function RequireOwner({ children }: { children: JSX.Element }) {
  const { initialising, isAuthenticated, isOwner, isBranchEmployee } = useAuth();
  const location = useLocation();

  if (initialising) {
    return (
      <div className="container py-5">
        <Spinner label="Checking your session" />
      </div>
    );
  }

  if (!isAuthenticated) {
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnTo=${returnTo}`} replace />;
  }

  if (isBranchEmployee) return <Navigate to="/owner/branches" replace />;
  if (!isOwner) return <NotAPartnerAccount />;

  return children;
}

function ListingCreateRoute() {
  const { isOwner, canManageHomeRentals } = useAuth();

  if (!isOwner && !canManageHomeRentals) return <Navigate to="/owner/branches" replace />;

  return <ListingEditorPage homeRentalOnly={!isOwner} />;
}

function NotAPartnerAccount() {
  return (
    <div className="container py-5" style={{ maxWidth: 560 }}>
      <h1 className="h4">This is the partner portal</h1>
      <p className="text-muted">
        You are signed in, but this account is not registered as a property owner or branch
        staff, so there is nothing here for it. If you are looking for somewhere to stay, use
        the main site.
      </p>
      <a className="btn btn-primary" href={CUSTOMER_SITE_URL}>
        Go to RoomKart
      </a>
    </div>
  );
}

function NotFound() {
  return (
    <div className="container py-5" style={{ maxWidth: 560 }}>
      <h1 className="h4">Page not found</h1>
      <p className="text-muted">That address does not exist in the partner portal.</p>
      <a className="btn btn-outline-secondary" href="/owner">
        Back to the dashboard
      </a>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        {/* This site is only the portal, so the root is simply a way in. The routes keep
            the /owner prefix they have always had, which is what every link inside the
            portal already points at. */}
        <Route index element={<Navigate to="/owner" replace />} />

        <Route path="login" element={<LoginPage />} />
        <Route path="register" element={<RegisterPage />} />
        <Route path="forgot-password" element={<ForgotPasswordPage />} />
        <Route path="reset-password" element={<ResetPasswordPage />} />

        {/* Guarded here as well as on every API endpoint it calls. */}
        <Route
          path="owner"
          element={
            <RequirePartner>
              <OwnerLayout />
            </RequirePartner>
          }
        >
          <Route index element={<OwnerDashboardPage />} />
          <Route path="account" element={<AccountPage />} />
          <Route path="businesses" element={<OwnerBusinessesPage />} />
          <Route path="branches" element={<OwnerBranchesPage />} />
          <Route
            path="branches/new"
            element={
              <RequireOwner>
                <BranchCreatePage />
              </RequireOwner>
            }
          />
          <Route
            path="branches/:businessId/staff"
            element={
              <RequireOwner>
                <BranchStaffPage />
              </RequireOwner>
            }
          />
          <Route path="listings" element={<OwnerListingsPage />} />
          <Route
            path="listings/new"
            element={<ListingCreateRoute />}
          />
          <Route path="listings/:propertyId/edit" element={<ListingEditorPage />} />
          <Route path="listings/:propertyId" element={<ListingManagePage />} />
          <Route path="enquiries" element={<OwnerEnquiriesPage />} />
          <Route path="billing" element={<OwnerBillingPage />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </AuthProvider>
  );
}
